import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.jsx'
import './index.css'
import 'leaflet/dist/leaflet.css'
import { initI18n } from './i18n.js'
import { setupErrorHandlers } from './services/logger'
import { MapSpotProvider } from './context/MapSpotContext.jsx'
import { NavigationHistoryProvider } from './context/NavigationHistoryContext.jsx'
import { initGA } from './utils/analytics'
import { initVersionCheck } from './utils/versionCheck'
import { initCacheBuster } from './utils/cacheBuster'
import { toast } from './services/toastService.jsx'

/**
 * Global Toast Testing API
 * 
 * Follows Architecture Guidelines:
 * - Observability: Provides debugging interface for toast system
 * - Modularity: Uses centralized toast service
 * 
 * Available in browser console:
 *   testToast.success() - Show success toast
 *   testToast.error() - Show error toast
 *   testToast.warning() - Show warning toast
 *   testToast.info() - Show info toast
 *   testToast.default() - Show default toast
 *   testToast.all() - Show all toast types
 *   testToast.clear() - Dismiss all toasts
 *   testToast.long() - Test long message
 */
if (typeof window !== 'undefined') {
  window.testToast = {
    success: (message = 'Success! Operation completed successfully.') => {
      toast.success(message)
    },
    error: (message = 'Error! Something went wrong.') => {
      toast.error(message)
    },
    warning: (message = 'Warning! Please check your input.') => {
      toast.warning(message)
    },
    info: (message = 'Info: This is an informational message.') => {
      toast.info(message)
    },
    default: (message = 'This is a default toast message.') => {
      toast.default(message)
    },
    custom: (message, options = {}) => {
      toast.default(message, options)
    },
    all: () => {
      toast.success('Success toast')
      setTimeout(() => toast.error('Error toast'), 300)
      setTimeout(() => toast.warning('Warning toast'), 600)
      setTimeout(() => toast.info('Info toast'), 900)
      setTimeout(() => toast.default('Default toast'), 1200)
    },
    clear: () => {
      toast.dismiss()
    },
    long: () => {
      toast.info('This is a very long toast message to test how the container handles longer content. It should wrap nicely and maintain the glassmorphism styling while being readable.')
    }
  }
}

// Setup global error handlers for logging
setupErrorHandlers()

// Initialize Google Analytics
initGA()

// Helper function to convert hex color to RGB values
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
    : null
}

// Helper function to darken RGB values for hover states
function darkenRgb(rgbString, percent = 0.15) {
  const [r, g, b] = rgbString.split(' ').map(Number)
  const factor = 1 - percent
  return `${Math.floor(r * factor)} ${Math.floor(g * factor)} ${Math.floor(b * factor)}`
}

// Helper function to lighten RGB values for backgrounds
function lightenRgb(rgbString, percent = 0.9) {
  const [r, g, b] = rgbString.split(' ').map(Number)
  const nr = Math.floor(r + (255 - r) * percent)
  const ng = Math.floor(g + (255 - g) * percent)
  const nb = Math.floor(b + (255 - b) * percent)
  return `${nr} ${ng} ${nb}`
}

// Set theme colors from environment variables
if (import.meta.env.VITE_PRIMARY_COLOR) {
  const primaryRgb = hexToRgb(import.meta.env.VITE_PRIMARY_COLOR)
  if (primaryRgb) {
    document.documentElement.style.setProperty('--color-primary', primaryRgb)
    document.documentElement.style.setProperty('--color-primary-700', darkenRgb(primaryRgb, 0.15))
    document.documentElement.style.setProperty('--color-primary-800', darkenRgb(primaryRgb, 0.25))
    document.documentElement.style.setProperty('--color-primary-50', lightenRgb(primaryRgb, 0.95))
    document.documentElement.style.setProperty('--color-primary-100', lightenRgb(primaryRgb, 0.9))
    document.documentElement.style.setProperty('--color-primary-200', lightenRgb(primaryRgb, 0.8))
  }
}

if (import.meta.env.VITE_SECONDARY_COLOR) {
  const secondaryRgb = hexToRgb(import.meta.env.VITE_SECONDARY_COLOR)
  if (secondaryRgb) {
    document.documentElement.style.setProperty('--color-secondary', secondaryRgb)
  }
}

if (import.meta.env.VITE_ACCENT_COLOR) {
  const accentRgb = hexToRgb(import.meta.env.VITE_ACCENT_COLOR)
  if (accentRgb) {
    document.documentElement.style.setProperty('--color-accent', accentRgb)
  }
}

// Unregister any service workers in development
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister()
    })
  })
}

// Initialize app with cache busting
async function initializeApp() {
  // CRITICAL: Clear old caches BEFORE rendering React app
  // This forces users on old cached versions to get the latest build
  await initCacheBuster()
  
  // Initialize i18n with correct language before rendering
  await initI18n()

  // Initialize version checking for PWA updates (after cache clear)
  initVersionCheck()
  
  // Render the React app
  createRoot(document.getElementById('root')).render(
  <StrictMode>
    <HelmetProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <NavigationHistoryProvider>
          <MapSpotProvider>
            <App />
          </MapSpotProvider>
        </NavigationHistoryProvider>
      </BrowserRouter>
    </HelmetProvider>
  </StrictMode>,
  )
}

// Start the app
initializeApp()